import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { getWeighingProcessBadgeClass, getWeighingProcessLabel } from '@/utils/weighingProcess';

interface PeseeDetailsModalProps {
  isOpen: boolean;
  pesee: any; // Type générique pour la pesée
  onClose: () => void;
  title?: string;
}

const PeseeDetailsModal: React.FC<PeseeDetailsModalProps> = ({
  isOpen,
  pesee,
  onClose,
  title
}) => {
  if (!isOpen || !pesee) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('fr-FR');
  };

  const formatWeight = (weight: number | null | undefined) => {
    return weight ? weight.toLocaleString('fr-FR') + ' kg' : '';
  };

  const formatNumber = (value: number | null | undefined) => {
    return value ? value.toLocaleString('fr-FR') : '';
  };

  const formatText = (value: string | null | undefined) => {
    return value || '';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* En-tête */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              {title || `Détails de la pesée ${pesee.reference || pesee.id}`}
              <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${getWeighingProcessBadgeClass(pesee)}`}>
                {getWeighingProcessLabel(pesee)}
              </span>
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>
        
        {/* Contenu */}
        <div className="px-6 py-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* Informations générales */}
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                  <i className="fas fa-info-circle text-blue-500 mr-2"></i>
                  Informations générales
                </h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Référence:</span>
                    <span className="text-gray-900 font-mono">{pesee.reference || pesee.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Date/Heure:</span>
                    <span className="text-gray-900">{formatDate(pesee.date_heure)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Type de pesée:</span>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      pesee.type_pesee === 'reception' ? 'bg-green-100 text-green-800' :
                      pesee.type_pesee === 'expedition' ? 'bg-blue-100 text-blue-800' :
                      pesee.type_pesee === 'transfert' ? 'bg-purple-100 text-purple-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {pesee.type_pesee?.toUpperCase() || ''}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-600">Parcours:</span>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getWeighingProcessBadgeClass(pesee)}`}>
                      {getWeighingProcessLabel(pesee)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Statut:</span>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      pesee.statut === 'COMPLETEE' ? 'bg-green-100 text-green-800' :
                      pesee.statut === 'TERMINE' ? 'bg-blue-100 text-blue-800' :
                      pesee.statut === 'ARCHIVE' ? 'bg-gray-100 text-gray-800' :
                      pesee.statut === 'EN_COURS' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-orange-100 text-orange-800'
                    }`}>
                      {pesee.statut || ''}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Produit:</span>
                    <span className="text-gray-900">{formatText(pesee.produit?.nom || pesee.produit_nom)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Numéro de lot:</span>
                    <span className="text-gray-900">{formatText(pesee.numero_lot)}</span>
                  </div>
                </div>
              </div>

              {/* Véhicule et transport */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                  <i className="fas fa-truck text-green-500 mr-2"></i>
                  Véhicule et Transport
                </h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Camion:</span>
                    <span className="text-gray-900">{pesee.camion || pesee.camion_immatriculation || ''}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Marque:</span>
                    <span className="text-gray-900">{pesee.camion_marque || ''}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Remorque:</span>
                    <span className="text-gray-900">{pesee.remorque_info?.immatriculation || pesee.remorque || ''}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Chauffeur:</span>
                    <span className="text-gray-900">{pesee.chauffeur_nom || pesee.chauffeur || ''}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Convoyeur:</span>
                    <span className="text-gray-900">{pesee.convoyeur_nom || pesee.convoyeur || ''}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Poids et mesures */}
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                  <i className="fas fa-weight text-orange-500 mr-2"></i>
                  Poids et Mesures
                </h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Poids brut:</span>
                    <span className="text-gray-900 font-semibold">{formatWeight(pesee.poids_brut)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Poids tare:</span>
                    <span className="text-gray-900 font-semibold">{formatWeight(pesee.poids_tare)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Poids net:</span>
                    <span className="text-gray-900 font-bold text-lg">{formatWeight(pesee.poids_net)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Poids acceptés:</span>
                    <span className="text-gray-900 font-semibold">{formatWeight(pesee.poids_acceptes)}</span>
                  </div>
                </div>
              </div>

              {/* Sacherie */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                  <i className="fas fa-boxes text-purple-500 mr-2"></i>
                  Sacherie
                </h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Nb sacs déclarés:</span>
                    <span className="text-gray-900">{formatNumber(pesee.nb_sacs_declares)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Nb sacs sains:</span>
                    <span className="text-gray-900">{formatNumber(pesee.nb_sacs_sains)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Nb sacs reconditionnés:</span>
                    <span className="text-gray-900">{formatNumber(pesee.nb_sacs_recond)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Nb sacs humides:</span>
                    <span className="text-gray-900">{formatNumber(pesee.nb_sacs_humides)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Nb sacs déchirés:</span>
                    <span className="text-gray-900">{formatNumber(pesee.nb_sacs_dechires)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Nb sacs rejetés:</span>
                    <span className="text-gray-900">{formatNumber(pesee.nb_sacs_rejetes)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Nb sacs acceptés:</span>
                    <span className="text-gray-900 font-semibold">{formatNumber(pesee.nb_sacs_acceptes)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Acteurs et partenaires */}
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                  <i className="fas fa-users text-indigo-500 mr-2"></i>
                  Acteurs et Partenaires
                </h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Client:</span>
                    <span className="text-gray-900">{pesee.client_nom || pesee.client || ''}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Fournisseur:</span>
                    <span className="text-gray-900">{pesee.fournisseur_nom || pesee.fournisseur || ''}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Transporteur:</span>
                    <span className="text-gray-900">{pesee.transporteur_nom || pesee.transporteur || ''}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Exportateur:</span>
                    <span className="text-gray-900">{pesee.exportateur_nom || pesee.exportateur || ''}</span>
                  </div>
                </div>
              </div>

              {/* Localisation */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                  <i className="fas fa-map-marker-alt text-red-500 mr-2"></i>
                  Localisation
                </h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Provenance:</span>
                    <span className="text-gray-900">{pesee.provenance || ''}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Destination:</span>
                    <span className="text-gray-900">{pesee.destination || ''}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Site:</span>
                    <span className="text-gray-900">{pesee.site_nom || pesee.site || ''}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Conditionnement et qualité */}
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                  <i className="fas fa-box text-teal-500 mr-2"></i>
                  Conditionnement
                </h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Type de conditionnement:</span>
                    <span className="text-gray-900">{pesee.conditionnement?.nom || pesee.conditionnement_info?.nom || ''}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Poids unitaire:</span>
                    <span className="text-gray-900">{formatWeight(pesee.conditionnement?.poids_unitaire || pesee.conditionnement_info?.poids_unitaire)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Capacité:</span>
                    <span className="text-gray-900">{formatNumber(pesee.conditionnement?.capacite || pesee.conditionnement_info?.capacite)}</span>
                  </div>
                </div>
              </div>

              {/* Observations */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                  <i className="fas fa-sticky-note text-yellow-500 mr-2"></i>
                  Observations
                </h4>
                <div className="text-sm text-gray-700">
                  {pesee.observations ? (
                    <div className="bg-white p-3 rounded border">
                      {pesee.observations}
                    </div>
                  ) : (
                    <span className="text-gray-500 italic">Aucune observation</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Pied de page */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default PeseeDetailsModal;
